/**
 * Home Analytics — mappers
 *
 * Pure DTO → view-model transforms. Each function is side-effect free and
 * defensive: statistics endpoints occasionally omit optional fields, so we
 * coalesce to safe defaults rather than render `NaN`/`undefined`.
 */

import type {InventoryStatisticsDTO} from '../../../inventory/models/inventory.dto';
import type {StoreStatisticsDTO} from '../../../stores/models/store.dto';
import type {UserStatisticsDTO} from '../../../users/models/user.dto';
import type {VisitStatisticsDTO} from '../../../visits/models/visit.dto';
import type {
  AnalyticsTone,
  BreakdownItem,
  InventoryProductsAnalytics,
  RankedItem,
  StatusSegment,
  StoresUsersAnalytics,
  VisitsAnalytics
} from '../models/homeAnalytics.model';
import type {ProductsStatsDTO} from '../models/homeAnalytics.dto';

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** Pull the first present string among candidate keys (loose breakdown rows). */
const pickLabel = (
  row: Record<string, unknown>,
  keys: string[],
  fallback: string
): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return fallback;
};

/**
 * Map a loose `[{ <name>, count }]` array to sorted, top-N breakdown items.
 * Rows missing every name key fall back to a language-neutral `#N` marker so the
 * data layer stays free of UI strings (no i18n leakage into the mapper).
 */
const toBreakdown = (
  rows: Array<Record<string, unknown>> | undefined,
  nameKeys: string[],
  limit = 5
): BreakdownItem[] =>
  (rows ?? [])
    .map((row, index) => ({
      label: pickLabel(row, nameKeys, `#${index + 1}`),
      value: num(row.count ?? row.total ?? row.value)
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

/** Tone for a visit/store status keyword (handles ar/en variants). */
const statusTone = (status: string): AnalyticsTone => {
  const key = status.toLowerCase();
  if (
    key.includes('approve') ||
    key.includes('active') ||
    key.includes('قبول') ||
    key.includes('نشط')
  ) {
    return 'success';
  }
  if (
    key.includes('pending') ||
    key.includes('انتظار') ||
    key.includes('معلق')
  ) {
    return 'warning';
  }
  if (
    key.includes('reject') ||
    key.includes('inactive') ||
    key.includes('رفض') ||
    key.includes('غير')
  ) {
    return 'error';
  }
  return 'neutral';
};

export const mapInventoryProducts = (
  inventory: InventoryStatisticsDTO,
  products: ProductsStatsDTO
): InventoryProductsAnalytics => ({
  totalProducts: num(inventory.total_products) || num(products.total_products),
  inStock: num(inventory.products_with_stock),
  lowStock: num(inventory.low_stock),
  outOfStock: num(inventory.out_of_stock),
  availableStock: num(inventory.available_stock),
  serialProducts:
    num(inventory.breakdown?.serial_products) ||
    num(products.products_with_serial),
  nonSerialProducts:
    num(inventory.breakdown?.non_serial_products) ||
    num(products.products_without_serial),
  productsByBrand: toBreakdown(products.products_by_brand, ['brand', 'brand_name', 'name']),
  productsByType: toBreakdown(products.products_by_type, ['type', 'type_name', 'name']),
  totalSerials: num(products.total_serials),
  availableSerials: num(products.available_serials)
});

export const mapVisits = (dto: VisitStatisticsDTO): VisitsAnalytics => {
  const statusSplit: StatusSegment[] = (dto.status_distribution ?? []).map(
    (row) => ({
      label: row.status,
      value: num(row.count),
      tone: statusTone(row.status)
    })
  );

  const topStores: RankedItem[] = (dto.top_stores ?? [])
    .slice(0, 5)
    .map((store) => ({
      id: store.store_id,
      name: store.store_name,
      subtitle: store.city,
      value: num(store.visit_count)
    }));

  const topVisitors: RankedItem[] = (dto.top_visitors ?? [])
    .slice(0, 5)
    .map((visitor) => ({
      id: visitor.visitor_id,
      name: visitor.visitor_name,
      subtitle: visitor.username,
      value: num(visitor.visit_count)
    }));

  return {
    total: statusSplit.reduce((sum, segment) => sum + segment.value, 0),
    statusSplit,
    topStores,
    topVisitors
  };
};

export const mapStoresUsers = (
  stores: StoreStatisticsDTO,
  users: UserStatisticsDTO
): StoresUsersAnalytics => ({
  totalStores: num(stores.totalStores),
  activeStores: num(stores.activeStores),
  inactiveStores: num(stores.inactiveStores),
  pendingPaymentStores: num(stores.pendingPaymentStores),
  lowStockStores: num(stores.lowStockStores),
  outOfStockStores: num(stores.outOfStockStores),
  storesActivePercentage: num(stores.activePercentage),
  totalUsers: num(users.total),
  activeUsers: num(users.active),
  marketDevelopers: num(users.marketDevelopers),
  usersActivePercentage: num(users.activePercentage),
  usersByRole: (users.roleDistribution ?? [])
    .map((row) => ({label: row.role, value: num(row.count)}))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)
});
